// =============================================================================
// BP-07-03: useResources Hook
// =============================================================================

import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { getResources, getFeaturedResources, getPopularResources } from '@/features/resources/api/resourcesApi';
import type { ResourceFilters, PaginationParams, Resource, PaginatedResponse } from '@/features/resources/types/resourcesTypes';
import { resourceQueryKeys } from '@/features/resources/types/resourcesTypes';

interface UseResourcesOptions {
  filters?: ResourceFilters;
  pagination?: PaginationParams;
  enabled?: boolean;
}

/**
 * Hook for fetching paginated resources with filters
 */
export function useResources(options: UseResourcesOptions = {}) {
  const { filters = {}, pagination = { page: 1, limit: 12 }, enabled = true } = options;

  return useQuery({
    queryKey: resourceQueryKeys.list(filters, pagination),
    queryFn: () => getResources(filters, pagination),
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook for infinite scroll resource loading
 */
export function useInfiniteResources(filters: ResourceFilters = {}, limit = 12) {
  return useInfiniteQuery({
    queryKey: [...resourceQueryKeys.lists(), 'infinite', filters],
    queryFn: ({ pageParam = 1 }) => getResources(filters, { page: pageParam, limit }),
    initialPageParam: 1,
    getNextPageParam: (lastPage: PaginatedResponse<Resource>) => {
      if (lastPage.hasNext) {
        return lastPage.page + 1;
      }
      return undefined;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook for fetching featured resources
 */
export function useFeaturedResources(limit = 6) {
  return useQuery({
    queryKey: [...resourceQueryKeys.featured(), limit],
    queryFn: () => getFeaturedResources(limit),
    staleTime: 10 * 60 * 1000, // 10 minutes - featured content changes less
  });
}

/**
 * Hook for fetching popular resources
 */
export function usePopularResources(limit = 10) {
  return useQuery({
    queryKey: [...resourceQueryKeys.popular(), limit],
    queryFn: () => getPopularResources(limit),
    staleTime: 5 * 60 * 1000,
  });
}

export default useResources;

